import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f87e31a4-f19a-4a3f-9c26-c5ad57e131e1/artifacts/vh1p37dl_IMG_0092.png";

export default function SplashScreen({ onComplete }) {
  const [isVisible, setIsVisible] = useState(true);

  const handleComplete = useCallback(() => {
    setIsVisible(false);
    sessionStorage.setItem('hasSeenSplash', 'true');
    if (onComplete) {
      onComplete();
    }
  }, [onComplete]);

  useEffect(() => {
    const hasSeenSplash = sessionStorage.getItem('hasSeenSplash');
    
    if (hasSeenSplash) {
      setIsVisible(false);
      if (onComplete) {
        onComplete();
      }
      return;
    }

    const timer = setTimeout(() => {
      handleComplete();
    }, 3000);

    const fallbackTimer = setTimeout(() => {
      handleComplete();
    }, 5000);

    return () => {
      clearTimeout(timer);
      clearTimeout(fallbackTimer);
    };
  }, [onComplete, handleComplete]);

  // Optimized blob animation - uses CSS for GPU acceleration
  const blobStyle = {
    willChange: 'transform, opacity',
    backfaceVisibility: 'hidden',
    perspective: 1000,
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460]"
          data-testid="splash-screen"
        >
          {/* Animated smoke/cloud background elements - Optimized */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Blob 1 - Cyan top-left */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ 
                opacity: [0, 0.4, 0.3, 0.4], 
                scale: [0.8, 1.1, 1, 1.05],
              }}
              transition={{ 
                duration: 6, 
                ease: "easeInOut",
                repeat: Infinity,
                repeatType: "reverse"
              }}
              className="absolute top-1/4 left-1/4 w-[250px] h-[250px] sm:w-[400px] sm:h-[400px] rounded-full"
              style={{ 
                ...blobStyle,
                background: 'radial-gradient(circle, rgba(0,212,255,0.6) 0%, rgba(0,212,255,0) 60%)',
                transform: 'translateZ(0)',
              }}
            />
            
            {/* Blob 2 - Pink bottom-right */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ 
                opacity: [0, 0.4, 0.3, 0.4], 
                scale: [0.8, 1.15, 0.95, 1.1],
              }}
              transition={{ 
                duration: 7, 
                ease: "easeInOut",
                repeat: Infinity,
                repeatType: "reverse",
                delay: 0.3
              }}
              className="absolute bottom-1/4 right-1/4 w-[250px] h-[250px] sm:w-[400px] sm:h-[400px] rounded-full"
              style={{ 
                ...blobStyle,
                background: 'radial-gradient(circle, rgba(255,20,147,0.6) 0%, rgba(255,20,147,0) 60%)',
                transform: 'translateZ(0)',
              }}
            />
            
            {/* Blob 3 - Purple center */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ 
                opacity: [0, 0.35, 0.25, 0.35], 
                scale: [0.6, 1.2, 1, 1.15]
              }}
              transition={{ 
                duration: 8, 
                ease: "easeInOut",
                repeat: Infinity,
                repeatType: "reverse",
                delay: 0.5
              }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] sm:w-[350px] sm:h-[350px] rounded-full"
              style={{ 
                ...blobStyle,
                background: 'radial-gradient(circle, rgba(139,92,246,0.5) 0%, rgba(139,92,246,0) 60%)',
                transform: 'translate(-50%, -50%) translateZ(0)',
              }}
            />
            
            {/* Blob 4 - Cyan accent */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: [0, 0.5, 0.25, 0.5],
                scale: [0.9, 1.3, 1, 1.2],
              }}
              transition={{ 
                duration: 5, 
                ease: "easeInOut",
                repeat: Infinity,
                repeatType: "reverse"
              }}
              className="absolute top-1/3 right-1/3 w-[150px] h-[150px] sm:w-[200px] sm:h-[200px] rounded-full"
              style={{ 
                ...blobStyle,
                background: 'radial-gradient(circle, rgba(0,212,255,0.5) 0%, rgba(0,212,255,0) 60%)',
                transform: 'translateZ(0)',
              }}
            />
            
            {/* Blob 5 - Pink accent */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: [0, 0.45, 0.2, 0.45],
                scale: [0.85, 1.25, 0.95, 1.15],
              }}
              transition={{ 
                duration: 5.5, 
                ease: "easeInOut",
                repeat: Infinity,
                repeatType: "reverse",
                delay: 0.8
              }}
              className="absolute bottom-1/3 left-1/3 w-[160px] h-[160px] sm:w-[220px] sm:h-[220px] rounded-full"
              style={{ 
                ...blobStyle,
                background: 'radial-gradient(circle, rgba(255,20,147,0.5) 0%, rgba(255,20,147,0) 60%)',
                transform: 'translateZ(0)',
              }}
            />
          </div>

          {/* Logo and text */}
          <div className="relative z-10 flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.5, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white/20 mb-8"
            >
              <img 
                src={LOGO_URL} 
                alt="Thrifty Curator" 
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              className="font-poppins text-3xl sm:text-4xl font-bold text-white mb-2 text-center"
            >
              Thrifty Curator<sup className="text-xs align-super opacity-50">™</sup>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
              className="text-white/60 text-sm sm:text-base tracking-widest uppercase"
            >
              Curated Resale Finds
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-10"
            >
              <div className="flex gap-2">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="w-2 h-2 bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] rounded-full"
                    animate={{
                      scale: [1, 1.5, 1],
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: i * 0.2,
                    }}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
